// src/components/admin/page-management/section-forms/GoogleMapsForm.js
import React, { useState } from 'react';

const GoogleMapsForm = ({ content, updateContent }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showMarkers, setShowMarkers] = useState(false);
  
  // Add a new location marker
  const addMarker = () => {
    const newMarkers = [...(content?.markers || [])];
    newMarkers.push({
      id: `marker_${Date.now()}`,
      address: process.env.REACT_APP_COMPANY_ADDRESS || '102-1127 14th St W, North Vancouver, BC, V7P 1J9',
      title: 'Our Location',
      description: 'Visit us today!',
      lat: '',
      lng: ''
    });
    updateContent('markers', newMarkers);
  };
  
  // Update a marker field
  const updateMarker = (index, field, value) => {
    const markers = [...(content?.markers || [])];
    markers[index] = {
      ...markers[index],
      [field]: value
    };
    updateContent('markers', markers);
  };
  
  // Remove a marker
  const removeMarker = (index) => {
    const markers = [...(content?.markers || [])];
    markers.splice(index, 1);
    updateContent('markers', markers);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Google Maps Configuration</h3>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Section Title (Optional)
        </label>
        <input
          type="text"
          className="w-full p-2 border rounded"
          value={content?.title || ''}
          onChange={(e) => updateContent('title', e.target.value)}
          placeholder="Our Location"
        />
        <p className="text-xs text-gray-500 mt-1">
          Leave blank if you don't want a title above the map.
        </p>
      </div>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Default Map Address
        </label>
        <input
          type="text"
          className="w-full p-2 border rounded"
          value={content?.address || process.env.REACT_APP_COMPANY_ADDRESS || '102-1127 14th St W, North Vancouver, BC, V7P 1J9'}
          onChange={(e) => updateContent('address', e.target.value)}
          placeholder="102-1127 14th St W, North Vancouver, BC, V7P 1J9"
        />
        <p className="text-xs text-gray-500 mt-1">
          This will be the default center of the map.
        </p>
      </div>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Map Height
        </label>
        <input
          type="text"
          className="w-full p-2 border rounded"
          value={content?.height || '400px'}
          onChange={(e) => updateContent('height', e.target.value)}
          placeholder="400px"
        />
        <p className="text-xs text-gray-500 mt-1">
          Set the height of the map (e.g., 400px, 500px, 50vh).
        </p>
      </div>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Map Zoom Level
        </label>
        <select
          className="w-full p-2 border rounded"
          value={content?.zoom || 15}
          onChange={(e) => updateContent('zoom', parseInt(e.target.value))}
        >
          {[...Array(19)].map((_, i) => (
            <option key={i + 1} value={i + 1}>
              {i + 1} - {i < 10 ? 'Far' : i < 15 ? 'Medium' : 'Close'}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Higher numbers zoom in closer. 15-17 is good for showing a single building.
        </p>
      </div>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Map Type
        </label>
        <select
          className="w-full p-2 border rounded"
          value={content?.mapType || 'roadmap'}
          onChange={(e) => updateContent('mapType', e.target.value)}
        >
          <option value="roadmap">Road Map (Default)</option>
          <option value="satellite">Satellite</option>
          <option value="hybrid">Hybrid (Satellite + Roads)</option>
          <option value="terrain">Terrain</option>
        </select>
      </div>
      
      <div className="mb-4">
        <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
          <input
            type="checkbox"
            className="mr-2 h-4 w-4"
            checked={content?.showControls !== false}
            onChange={(e) => updateContent('showControls', e.target.checked)}
          />
          Show Map Controls
        </label>
        <p className="text-xs text-gray-500 mt-1 ml-6">
          Enables zoom, pan, and street view controls on the map.
        </p>
      </div>
      
      <div className="mb-4">
        <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
          <input
            type="checkbox"
            className="mr-2 h-4 w-4"
            checked={content?.allowFullscreen !== false}
            onChange={(e) => updateContent('allowFullscreen', e.target.checked)}
          />
          Allow Fullscreen Option
        </label>
      </div>
      
      <div className="mb-4">
        <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
          <input
            type="checkbox"
            className="mr-2 h-4 w-4"
            checked={content?.allowStreetView !== false}
            onChange={(e) => updateContent('allowStreetView', e.target.checked)}
          />
          Allow Street View
        </label>
      </div>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Map Load Method
        </label>
        <select
          className="w-full p-2 border rounded"
          value={content?.loadMethod || 'embed'}
          onChange={(e) => updateContent('loadMethod', e.target.value)}
        >
          <option value="embed">Simple Embed (Less Customizable)</option>
          <option value="javascript">JavaScript API (More Customizable)</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          The JavaScript API option offers more customization but requires an API key configuration.
        </p>
      </div>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Placeholder Image URL
        </label>
        <input
          type="text"
          className="w-full p-2 border rounded"
          value={content?.placeholderImage || '/images/map-placeholder.jpg'}
          onChange={(e) => updateContent('placeholderImage', e.target.value)}
          placeholder="/images/map-placeholder.jpg"
        />
        <p className="text-xs text-gray-500 mt-1">
          Image to display while the map is loading or if it fails.
        </p>
      </div>
      
      {/* Multiple Markers Configuration */}
      <div className="border-t border-gray-200 pt-4">
        <button
          type="button"
          className="text-sm text-blue-600 hover:text-blue-800 mb-4"
          onClick={() => setShowMarkers(!showMarkers)}
        >
          {showMarkers ? 'Hide Location Markers' : 'Configure Multiple Location Markers'}
        </button>
        
        {showMarkers && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-800">Location Markers</h4>
              <button
                type="button"
                onClick={addMarker}
                className="text-sm bg-blue-50 hover:bg-blue-100 text-blue-600 py-1 px-3 rounded border border-blue-200"
              >
                + Add Marker
              </button>
            </div>
            
            {(content?.markers || []).length === 0 ? (
              <p className="text-sm text-gray-500 italic p-4 bg-gray-50 rounded">
                No custom markers added. Add a marker to show specific locations on the map.
              </p>
            ) : (
              (content?.markers || []).map((marker, index) => (
                <div key={marker.id} className="border rounded-lg p-4 mb-4 bg-gray-50">
                  <div className="flex justify-between items-center mb-3">
                    <h5 className="font-medium">{marker.title || `Location ${index + 1}`}</h5>
                    <button 
                      type="button"
                      onClick={() => removeMarker(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Address
                      </label>
                      <input
                        type="text"
                        className="w-full p-2 border rounded text-sm"
                        value={marker.address}
                        onChange={(e) => updateMarker(index, 'address', e.target.value)}
                        placeholder="Enter address"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Marker Title
                      </label>
                      <input
                        type="text"
                        className="w-full p-2 border rounded text-sm"
                        value={marker.title}
                        onChange={(e) => updateMarker(index, 'title', e.target.value)}
                        placeholder="Location title"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        className="w-full p-2 border rounded text-sm"
                        value={marker.description}
                        onChange={(e) => updateMarker(index, 'description', e.target.value)}
                        placeholder="Location description"
                        rows="2"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Latitude (Optional)
                        </label>
                        <input
                          type="text"
                          className="w-full p-2 border rounded text-sm"
                          value={marker.lat}
                          onChange={(e) => updateMarker(index, 'lat', e.target.value)}
                          placeholder="e.g. 49.3229"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Longitude (Optional)
                        </label>
                        <input
                          type="text"
                          className="w-full p-2 border rounded text-sm"
                          value={marker.lng}
                          onChange={(e) => updateMarker(index, 'lng', e.target.value)}
                          placeholder="e.g. -123.1235"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Marker Icon URL (Optional)
                      </label>
                      <input
                        type="text"
                        className="w-full p-2 border rounded text-sm"
                        value={marker.iconUrl || ''}
                        onChange={(e) => updateMarker(index, 'iconUrl', e.target.value)}
                        placeholder="/images/custom-marker.png"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Leave blank to use the default Google Maps marker.
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
            
            <p className="text-xs text-gray-500 mt-2">
              Note: For precise marker placement, you can enter latitude and longitude coordinates. 
              Otherwise, Google Maps will geocode the address automatically.
            </p>
          </div>
        )}
      </div>
      
      {/* Advanced Options */}
      <div className="border-t border-gray-200 pt-4">
        <button
          type="button"
          className="text-sm text-blue-600 hover:text-blue-800 mb-4"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced ? 'Hide Advanced Options' : 'Show Advanced Options'}
        </button>
        
        {showAdvanced && (
          <div className="space-y-4">
            <h4 className="font-medium text-gray-800 mb-3">Advanced Map Options</h4>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Map Style
              </label>
              <select
                className="w-full p-2 border rounded"
                value={content?.mapStyle || 'default'}
                onChange={(e) => updateContent('mapStyle', e.target.value)}
              >
                <option value="default">Default</option>
                <option value="silver">Silver</option>
                <option value="retro">Retro</option>
                <option value="dark">Dark</option>
                <option value="night">Night</option>
                <option value="aubergine">Aubergine</option>
                <option value="custom">Custom JSON Style</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Choose a predefined style for the map appearance (JavaScript API only).
              </p>
            </div>
            
            {content?.mapStyle === 'custom' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Custom Map Style (JSON)
                </label>
                <textarea
                  className="w-full p-2 border rounded font-mono text-sm"
                  value={content?.customMapStyle || '[]'}
                  onChange={(e) => updateContent('customMapStyle', e.target.value)}
                  placeholder='[{"featureType":"all","elementType":"labels.text.fill","stylers":[{"saturation":36},{"color":"#333333"},{"lightness":40}]}]'
                  rows="6"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Paste custom map style JSON from <a href="https://mapstyle.withgoogle.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google's Map Style Wizard</a>.
                </p>
              </div>
            )}
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Border Radius
              </label>
              <select
                className="w-full p-2 border rounded"
                value={content?.borderRadius || 'rounded-xl'}
                onChange={(e) => updateContent('borderRadius', e.target.value)}
              >
                <option value="rounded-none">None</option>
                <option value="rounded">Small</option>
                <option value="rounded-md">Medium</option>
                <option value="rounded-lg">Large</option>
                <option value="rounded-xl">Extra Large</option>
              </select>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Shadow Style
              </label>
              <select
                className="w-full p-2 border rounded"
                value={content?.shadowStyle || 'shadow-lg'}
                onChange={(e) => updateContent('shadowStyle', e.target.value)}
              >
                <option value="shadow-none">None</option>
                <option value="shadow-sm">Small</option>
                <option value="shadow">Medium</option>
                <option value="shadow-md">Large</option>
                <option value="shadow-lg">Extra Large</option>
                <option value="shadow-xl">2XL</option>
              </select>
            </div>
            
            <div className="mb-4">
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                <input
                  type="checkbox"
                  className="mr-2 h-4 w-4"
                  checked={content?.showTraffic === true}
                  onChange={(e) => updateContent('showTraffic', e.target.checked)}
                />
                Show Traffic Layer
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-6">
                Display real-time traffic information (JavaScript API only).
              </p>
            </div>
            
            <div className="mb-4">
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                <input
                  type="checkbox"
                  className="mr-2 h-4 w-4"
                  checked={content?.showInfoWindowsOnLoad === true}
                  onChange={(e) => updateContent('showInfoWindowsOnLoad', e.target.checked)}
                />
                Show Info Windows on Load
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-6">
                Automatically open info windows when the map loads (JavaScript API only).
              </p>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Custom CSS (Advanced)
              </label>
              <textarea
                className="w-full p-2 border rounded font-mono text-sm"
                value={content?.customCss || ''}
                onChange={(e) => updateContent('customCss', e.target.value)}
                placeholder=".map-container { /* custom styles */ }"
                rows="4"
              />
              <p className="text-xs text-gray-500 mt-1">
                Add custom CSS to further customize your map container (for advanced users).
              </p>
            </div>
          </div>
        )}
      </div>
      
      <div className="bg-blue-50 p-4 rounded-md mb-6">
        <h4 className="text-blue-800 font-medium mb-2">Important Notes</h4>
        <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
          <li>Advanced features like custom styles require using the JavaScript API option</li>
          <li>For multiple markers, the JavaScript API is recommended</li>
          <li>The placeholder image will show until the map is fully loaded</li>
        </ul>
      </div>
    </div>
  );
};

export default GoogleMapsForm;







// // src/components/admin/page-management/section-forms/GoogleMapsForm.js
// import React from 'react';

// const GoogleMapsForm = ({ content, updateContent }) => {
//   return (
//     <div className="space-y-4">
//       <h3 className="text-lg font-medium">Google Maps Configuration</h3>
      
//       <div className="mb-4">
//         <label className="block text-sm font-medium text-gray-700 mb-1">
//           Map Address
//         </label>
//         <input
//           type="text"
//           className="w-full p-2 border rounded"
//           value={content?.address || ''}
//           onChange={(e) => updateContent('address', e.target.value)}
//           placeholder="102-1127 14th St W, North Vancouver, BC, V7P 1J9"
//         />
//         <p className="text-xs text-gray-500 mt-1">
//           Enter the full address to display on the map.
//         </p>
//       </div>
      
//       <div className="mb-4">
//         <label className="block text-sm font-medium text-gray-700 mb-1">
//           Map Height
//         </label>
//         <input
//           type="text"
//           className="w-full p-2 border rounded"
//           value={content?.height || '400px'}
//           onChange={(e) => updateContent('height', e.target.value)}
//           placeholder="400px"
//         />
//         <p className="text-xs text-gray-500 mt-1">
//           Set the height of the map (e.g., 400px, 500px, 50vh).
//         </p>
//       </div>
      
//       <div className="mb-4">
//         <label className="block text-sm font-medium text-gray-700 mb-1">
//           Placeholder Image URL
//         </label>
//         <input
//           type="text"
//           className="w-full p-2 border rounded"
//           value={content?.placeholderImage || '/images/map.jpg'}
//           onChange={(e) => updateContent('placeholderImage', e.target.value)}
//           placeholder="/images/map.jpg"
//         />
//         <p className="text-xs text-gray-500 mt-1">
//           Image to display while the map is loading.
//         </p>
//       </div>
      
//       <div className="bg-blue-50 p-4 rounded-md mb-6">
//         <h4 className="text-blue-800 font-medium mb-2">Important Notes</h4>
//         <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
//           <li>Make sure your API key is set up correctly in the backend</li>
//           <li>The map will load dynamically after the page loads</li>
//           <li>The placeholder image will show until the map is fully loaded</li>
//         </ul>
//       </div>
//     </div>
//   );
// };

// export default GoogleMapsForm;